// Миниатюра чека в списке.
//
// Просто `<img src={адрес}>` здесь НЕ работает, и это не мелочь: содержимое чека отдаётся
// только по токену, а тег изображения заголовок входа не несёт. Браузер получает отказ и
// рисует сломанную картинку — на очереди приёмки это выглядит как «чеков нет вовсе».
// Поймано живым прогоном 15.08.2026.
//
// Поэтому байты забираются той же дверью, что и всё остальное (подпись токеном, единая точка
// 401, разбор конверта ошибки), и показываются через локальную ссылку на объект.

import { useEffect, useState } from 'react';

import { RiFileList2Line, RiImageLine, RiFilePaper2Line } from '@remixicon/react';

import { requestFile } from '@/shared/api/client';
import { путьФайла } from '@/shared/ui/attachment';

export interface ReceiptThumbProps {
  /** Адрес миниатюры с сервера. `null` — чека у заявки нет вовсе. */
  url: string | null;
  alt: string;
}

export function ReceiptThumb({ url, alt }: ReceiptThumbProps) {
  const [ссылка, setСсылка] = useState<string | null>(null);
  const [сломан, setСломан] = useState(false);
  // Третье состояние, а не второе. Сервер на «миниатюры у файла нет» отвечает 204 без
  // содержимого, и это НЕ ошибка: у pdf, ролика и снимка, который не смог прочитать
  // уменьшитель, миниатюры не бывает по устройству (`server/app/services/files.py`,
  // `download_thumb`). Ответ при этом успешный, и до 03.09.2026 пустое тело превращалось в
  // пустой blob, а из него — в СЛОМАННУЮ КАРТИНКУ браузера с подписью «Чек».
  //
  // То есть компонент, написанный ровно затем, чтобы этого значка никто не увидел, сам его и
  // рисовал. Видно было на кадре `кабинет-очередь-приёмки.png`: две карточки из четырёх, и
  // читается как «система сломана» на экране, где финотдел принимает деньги.
  const [безМиниатюры, setБезМиниатюры] = useState(false);

  useEffect(() => {
    if (url === null) {
      return;
    }
    let живо = true;
    let созданная: string | null = null;
    // Сброс на смену адреса: без него на быстрой перелистовке в списке остаётся видна
    // миниатюра ПРЕДЫДУЩЕГО чека, и человек принимает не тот расход.
    setСсылка(null);
    setСломан(false);
    setБезМиниатюры(false);
    void requestFile(путьФайла(url))
      .then(({ blob }) => {
        if (!живо) {
          return;
        }
        // Пустое тело — это ответ «миниатюры нет», а не картинка нулевого размера. Отдать его
        // тегу изображения значит показать человеку значок поломки вместо ответа.
        if (blob.size === 0) {
          setБезМиниатюры(true);
          return;
        }
        созданная = URL.createObjectURL(blob);
        setСсылка(созданная);
      })
      .catch(() => {
        if (живо) {
          setСломан(true);
        }
      });
    return () => {
      живо = false;
      if (созданная !== null) {
        URL.revokeObjectURL(созданная);
      }
    };
  }, [url]);

  if (url === null) {
    // «Без чека» — осознанная отметка человека, а не отсутствие данных. Пустой серый квадрат
    // читался бы как «изображение не загрузилось», и это другое.
    return (
      <span
        className='flex size-10 shrink-0 items-center justify-center rounded-md border border-dashed border-stroke-soft-200 text-text-soft-400'
        role='img'
        aria-label='Расход без подтверждающего документа'
      >
        <RiFileList2Line className='size-4' />
      </span>
    );
  }

  if (безМиниатюры) {
    // Файл на месте, показать в списке нечего: так бывает у pdf и у ролика. Значок другой,
    // чем у «без чека» — там документа нет вовсе, а здесь он есть и открывается.
    return (
      <span
        className='flex size-10 shrink-0 items-center justify-center rounded-md border border-stroke-soft-200 text-text-soft-400'
        role='img'
        aria-label='Документ приложен, показать его уменьшенным нечем — откройте заявку'
      >
        <RiFilePaper2Line className='size-4' />
      </span>
    );
  }

  if (сломан) {
    return (
      <span
        className='flex size-10 shrink-0 items-center justify-center rounded-md border border-stroke-soft-200 text-text-soft-400'
        role='img'
        aria-label='Изображение чека не читается'
      >
        <RiImageLine className='size-4' />
      </span>
    );
  }

  if (ссылка === null) {
    return <span className='size-10 shrink-0 animate-pulse rounded-md bg-bg-weak-50' />;
  }

  return (
    <img
      src={ссылка}
      alt={alt}
      // Последняя защита: байты приехали, а картинкой не оказались. Без этого браузер рисует
      // свой значок поломки с подписью — то самое, чего компонент и должен не допускать.
      onError={() => setСломан(true)}
      className='size-10 shrink-0 rounded-md border border-stroke-soft-200 object-cover'
    />
  );
}
